import { computed, watch } from 'vue'
import type { Ref } from 'vue'
import { useUserPreferences } from '~/composables/ui/useUserPreferences'

type UseWelcomeSplashOptions = {
  loggedIn: Ref<boolean>
}

function timeoutAfterMs(ms: number): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`welcome splash timeout after ${ms}ms`)), ms)
  })
}

export function useWelcomeSplash(options: UseWelcomeSplashOptions) {
  const { prefs, isReady, setWelcomeSplashDone } = useUserPreferences()
  const isWelcomeResolved = useState<boolean>('welcome-splash.resolved', () => false)
  const shouldShowWelcomeSplash = useState<boolean>('welcome-splash.visible', () => false)
  const splashSessionHandled = useState<boolean>('welcome-splash.session-handled', () => false)
  const isPersistingWelcome = useState<boolean>('welcome-splash.persisting', () => false)

  watch(
    () => options.loggedIn.value,
    (isLoggedInNow) => {
      if (isLoggedInNow) {
        return
      }
      shouldShowWelcomeSplash.value = false
      isWelcomeResolved.value = true
      splashSessionHandled.value = false
      isPersistingWelcome.value = false
    },
    { immediate: true }
  )

  watch(
    () => ({
      ready: isReady.value,
      loggedIn: options.loggedIn.value,
      welcomeSplashDone: prefs.value.welcomeSplashDone === true,
      alreadyHandled: splashSessionHandled.value,
    }),
    ({ ready, loggedIn, welcomeSplashDone, alreadyHandled }) => {
      if (!loggedIn) {
        return
      }
      if (!ready) {
        isWelcomeResolved.value = false
        shouldShowWelcomeSplash.value = false
        return
      }
      if (welcomeSplashDone || alreadyHandled) {
        shouldShowWelcomeSplash.value = false
        isWelcomeResolved.value = true
        return
      }
      shouldShowWelcomeSplash.value = true
      isWelcomeResolved.value = false
    },
    { immediate: true }
  )

  async function onWelcomeSplashFinished(): Promise<void> {
    if (!options.loggedIn.value || splashSessionHandled.value) {
      shouldShowWelcomeSplash.value = false
      isWelcomeResolved.value = true
      return
    }
    splashSessionHandled.value = true
    shouldShowWelcomeSplash.value = false
    isPersistingWelcome.value = true
    try {
      await Promise.race([
        setWelcomeSplashDone(true),
        timeoutAfterMs(2500),
      ])
    } catch (error) {
      console.error('[welcome-splash] persistance preference failed, fallback to continue', error)
    } finally {
      isPersistingWelcome.value = false
      isWelcomeResolved.value = true
    }
  }

  return {
    isWelcomeResolved: computed(() => isWelcomeResolved.value),
    shouldShowWelcomeSplash: computed(() => shouldShowWelcomeSplash.value),
    isPersistingWelcome: computed(() => isPersistingWelcome.value),
    onWelcomeSplashFinished,
  }
}

